import { useEffect, useState } from "react";
import type {
  FamilyDashboardResponseDto,
  FamilyScheduleItemDto,
  FamilyScheduleResponseDto,
  FamilyStudentsResponseDto,
  NotificationInboxItemDto,
  NotificationPrefsDto,
  NotificationsResponseDto,
  ViewerVideosResponseDto
} from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import { fetchFamilyDashboard, fetchFamilySchedule, fetchFamilyStudents } from "../../shared/api/family";
import { fetchFamilyNotifications, updateFamilyNotificationPrefs } from "../../shared/api/notification";
import { fetchFamilyVideos } from "../../shared/api/video";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const familyLinks = [
  { to: "/t/:tenantSlug/home", label: "ホーム" },
  { to: "/t/:tenantSlug/my-schedule", label: "予定" },
  { to: "/t/:tenantSlug/videos", label: "動画" },
  { to: "/t/:tenantSlug/notifications", label: "通知" }
];

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

type NotificationPrefsFormState = {
  inAppEnabled: boolean;
  emailEnabled: boolean;
  criticalEmailEnabled: boolean;
  highEmailEnabled: boolean;
  serviceMessageEnabled: boolean;
  digestEnabled: boolean;
  quietHours: string;
};

function createNotificationPrefsFormState(prefs: NotificationPrefsDto): NotificationPrefsFormState {
  return {
    inAppEnabled: prefs.inAppEnabled,
    emailEnabled: prefs.emailEnabled,
    criticalEmailEnabled: prefs.criticalEmailEnabled,
    highEmailEnabled: prefs.highEmailEnabled,
    serviceMessageEnabled: prefs.serviceMessageEnabled,
    digestEnabled: prefs.digestEnabled,
    quietHours: prefs.quietHours ?? ""
  };
}

function NotificationItemCard({ item }: { item: NotificationInboxItemDto }) {
  return (
    <article
      style={{
        border: "1px solid #e5e5e5",
        borderRadius: 12,
        padding: 12,
        display: "grid",
        gap: 8
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ display: "grid", gap: 4 }}>
          <strong>{item.title}</strong>
          <span style={{ fontSize: 13, opacity: 0.8 }}>{formatDateTime(item.createdAt)}</span>
        </div>
        <span
          style={{
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            background: item.priority === "high" || item.priority === "critical" ? "#fff1f1" : "#f2f2f2"
          }}
        >
          {item.priority}
        </span>
      </div>

      <div style={{ fontSize: 14 }}>{item.body}</div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
        <span style={{ padding: "4px 8px", borderRadius: 999, background: "#f6f6f6" }}>{item.category}</span>
        {item.relatedStudentIds.map((studentId) => (
          <span key={studentId} style={{ padding: "4px 8px", borderRadius: 999, background: "#f6f6f6" }}>
            {studentId}
          </span>
        ))}
        {item.readAt ? <span style={{ padding: "4px 8px", borderRadius: 999, background: "#eef6ff" }}>既読</span> : null}
      </div>

      {item.targetType && item.targetId ? (
        <div style={{ fontSize: 13, opacity: 0.8 }}>
          target: {item.targetType} / {item.targetId}
        </div>
      ) : null}
    </article>
  );
}

function ScheduleItemCard({ item }: { item: FamilyScheduleItemDto }) {
  return (
    <article
      style={{
        border: "1px solid #e5e5e5",
        borderRadius: 12,
        padding: 12,
        display: "grid",
        gap: 8
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div>
          <strong>{item.title}</strong>
          <div style={{ fontSize: 14, opacity: 0.8 }}>{formatDateTime(item.startAt)} - {formatDateTime(item.endAt)}</div>
        </div>
        <span
          style={{
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            background: item.itemType === "event" ? "#eef6ff" : "#f2f2f2"
          }}
        >
          {item.itemType}
        </span>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
        {item.studentDisplayNames.map((studentName) => (
          <span key={studentName} style={{ padding: "4px 8px", borderRadius: 999, background: "#f6f6f6" }}>
            {studentName}
          </span>
        ))}
      </div>

      {item.locationLabel ? <div style={{ fontSize: 14 }}>場所: {item.locationLabel}</div> : null}
      {item.displayFlags.showTeacherNames ? (
        <div style={{ fontSize: 14 }}>講師: {item.teacherNameSnapshots.join(", ")}</div>
      ) : null}
      {item.displayFlags.hasPublicNote && item.publicNote ? (
        <div style={{ fontSize: 14 }}>お知らせ: {item.publicNote}</div>
      ) : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
        {item.displayFlags.isCancelled ? (
          <span style={{ padding: "4px 8px", borderRadius: 999, background: "#fff1f1" }}>休講</span>
        ) : null}
        {item.displayFlags.isChanged ? (
          <span style={{ padding: "4px 8px", borderRadius: 999, background: "#fff6e6" }}>変更あり</span>
        ) : null}
      </div>
    </article>
  );
}

function FamilyAccessState({
  status,
  errorMessage,
  hasParentRole,
  tenantSlug
}: {
  status: ReturnType<typeof useSessionBootstrap>["status"];
  errorMessage: string | null;
  hasParentRole: boolean;
  tenantSlug: string;
}) {
  if (status === "loading") {
    return <p>セッションを確認しています。</p>;
  }

  if (status === "unauthenticated") {
    return (
      <p>
        family 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock family login を実行してください。
      </p>
    );
  }

  if (status === "error") {
    return <p>セッション取得に失敗しました: {errorMessage}</p>;
  }

  if (status === "authenticated" && !hasParentRole) {
    return (
      <p>
        parent role が必要です。<Link to={`/t/${tenantSlug}/link-student`}>link-student</Link> から紐付けしてください。
      </p>
    );
  }

  return null;
}

export function FamilyHomePage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [dashboard, setDashboard] = useState<FamilyDashboardResponseDto | null>(null);
  const [students, setStudents] = useState<FamilyStudentsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole) {
      return;
    }

    let cancelled = false;

    async function loadHome() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const [nextDashboard, nextStudents] = await Promise.all([
          fetchFamilyDashboard(tenantSlug),
          fetchFamilyStudents(tenantSlug)
        ]);

        if (cancelled) {
          return;
        }

        setDashboard(nextDashboard);
        setStudents(nextStudents);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "family dashboard の取得に失敗しました");
      }
    }

    void loadHome();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, tenantSlug]);

  return (
    <PageScaffold
      title="Family Home"
      description="linked student の一覧と、兄弟姉妹まとめの次回予定を表示します。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FamilyAccessState
          status={status}
          errorMessage={errorMessage}
          hasParentRole={hasParentRole}
          tenantSlug={tenantSlug}
        />

        {loadState === "loading" ? <p>family dashboard を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {students ? (
          <section style={{ display: "grid", gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>子ども一覧</h2>
            {students.items.map((student) => (
              <Link
                key={student.studentId}
                to={`/t/${tenantSlug}/students/${student.studentId}`}
                style={{
                  display: "grid",
                  gap: 6,
                  padding: 12,
                  border: "1px solid #e5e5e5",
                  borderRadius: 12,
                  textDecoration: "none",
                  color: "inherit"
                }}
              >
                <strong>{student.displayName}</strong>
                <span style={{ fontSize: 14, opacity: 0.8 }}>次回予定: {student.nextScheduledAt ? formatDateTime(student.nextScheduledAt) : "なし"}</span>
                <span style={{ fontSize: 14, opacity: 0.8 }}>予定件数: {student.upcomingCount}</span>
              </Link>
            ))}
          </section>
        ) : null}

        {dashboard ? (
          <section style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>次回予定</h2>
              <Link to={`/t/${tenantSlug}/my-schedule`}>予定一覧へ</Link>
            </div>
            {dashboard.upcomingItems.length === 0 ? <p style={{ margin: 0 }}>表示できる予定はありません。</p> : null}
            {dashboard.upcomingItems.map((item) => (
              <ScheduleItemCard key={`${item.itemType}:${item.itemId}`} item={item} />
            ))}
          </section>
        ) : null}
      </div>
    </PageScaffold>
  );
}

export function FamilySchedulePage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [schedule, setSchedule] = useState<FamilyScheduleResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole) {
      return;
    }

    let cancelled = false;

    async function loadSchedule() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextSchedule = await fetchFamilySchedule(tenantSlug);

        if (cancelled) {
          return;
        }

        setSchedule(nextSchedule);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "family schedule の取得に失敗しました");
      }
    }

    void loadSchedule();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, tenantSlug]);

  return (
    <PageScaffold
      title="Family Schedule"
      description="当月から翌月末までの兄弟姉妹まとめ予定です。month-grouped response をそのまま表示します。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FamilyAccessState
          status={status}
          errorMessage={errorMessage}
          hasParentRole={hasParentRole}
          tenantSlug={tenantSlug}
        />

        {loadState === "loading" ? <p>family schedule を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {schedule ? (
          <>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
              表示範囲: {schedule.rangeStart.slice(0, 10)} - {schedule.rangeEnd.slice(0, 10)}
            </p>
            {schedule.monthGroups.map((monthGroup) => (
              <section key={monthGroup.yearMonth} style={{ display: "grid", gap: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>{monthGroup.label}</h2>
                {monthGroup.items.map((item) => (
                  <ScheduleItemCard key={`${monthGroup.yearMonth}:${item.itemType}:${item.itemId}`} item={item} />
                ))}
              </section>
            ))}
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}

export function FamilyVideosPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [videos, setVideos] = useState<ViewerVideosResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole) {
      return;
    }

    let cancelled = false;

    async function loadVideos() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextVideos = await fetchFamilyVideos(tenantSlug);

        if (cancelled) {
          return;
        }

        setVideos(nextVideos);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "family videos の取得に失敗しました");
      }
    }

    void loadVideos();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, tenantSlug]);

  return (
    <PageScaffold
      title="Family Videos"
      description="linked student に紐づく公開済み動画を表示します。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FamilyAccessState
          status={status}
          errorMessage={errorMessage}
          hasParentRole={hasParentRole}
          tenantSlug={tenantSlug}
        />

        {loadState === "loading" ? <p>family videos を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {videos?.items.length === 0 ? <p>視聴可能な動画はありません。</p> : null}

        {videos?.items.map((video) => (
          <Link
            key={video.videoId}
            to={`/t/${tenantSlug}/videos/${video.videoId}`}
            style={{
              display: "grid",
              gap: 6,
              padding: 12,
              border: "1px solid #e5e5e5",
              borderRadius: 12,
              textDecoration: "none",
              color: "inherit"
            }}
          >
            <strong>{video.title ?? "タイトル未設定"}</strong>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              {video.videoPurpose} / {video.targetType} / {video.targetLabel}
            </div>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              公開期間: {video.publishFrom ? video.publishFrom.slice(0, 10) : "未設定"} - {video.publishTo ? video.publishTo.slice(0, 10) : "未設定"}
            </div>
            {video.description ? <div style={{ fontSize: 14 }}>{video.description}</div> : null}
          </Link>
        ))}
      </div>
    </PageScaffold>
  );
}

export function NotificationsPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [prefsForm, setPrefsForm] = useState<NotificationPrefsFormState | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasParentRole = Boolean(session?.member?.roles.includes("parent"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    if (status !== "authenticated" || !hasParentRole) {
      return;
    }

    let cancelled = false;

    async function loadNotifications() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextNotifications = await fetchFamilyNotifications(tenantSlug);

        if (cancelled) {
          return;
        }

        setNotifications(nextNotifications);
        setPrefsForm(nextNotifications.prefs ? createNotificationPrefsFormState(nextNotifications.prefs) : null);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        setLoadState("error");
        setLoadError(error instanceof Error ? error.message : "notifications の取得に失敗しました");
      }
    }

    void loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [hasParentRole, status, tenantSlug]);

  function updateBooleanField(field: Exclude<keyof NotificationPrefsFormState, "quietHours">, value: boolean) {
    setPrefsForm((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        [field]: value
      };
    });
  }

  async function handleSavePrefs() {
    if (!prefsForm) {
      return;
    }

    setSaveState("saving");
    setSaveError(null);

    try {
      const nextPrefs = await updateFamilyNotificationPrefs(tenantSlug, {
        inAppEnabled: prefsForm.inAppEnabled,
        emailEnabled: prefsForm.emailEnabled,
        criticalEmailEnabled: prefsForm.criticalEmailEnabled,
        highEmailEnabled: prefsForm.highEmailEnabled,
        serviceMessageEnabled: prefsForm.serviceMessageEnabled,
        digestEnabled: prefsForm.digestEnabled,
        quietHours: prefsForm.quietHours.trim().length > 0 ? prefsForm.quietHours.trim() : null
      });

      setNotifications((current) => (current ? { ...current, prefs: nextPrefs } : current));
      setPrefsForm(createNotificationPrefsFormState(nextPrefs));
      setSaveState("saved");
    } catch (error) {
      setSaveState("error");
      setSaveError(error instanceof Error ? error.message : "通知設定の更新に失敗しました");
    }
  }

  return (
    <PageScaffold
      title="Notifications"
      description="family 向けのアプリ内通知と通知設定です。"
      links={familyLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        <FamilyAccessState
          status={status}
          errorMessage={errorMessage}
          hasParentRole={hasParentRole}
          tenantSlug={tenantSlug}
        />

        {loadState === "loading" ? <p>notifications を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {prefsForm ? (
          <section style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>通知設定</h2>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.inAppEnabled}
                onChange={(event) => updateBooleanField("inAppEnabled", event.currentTarget.checked)}
              />
              in-app 通知
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.emailEnabled}
                onChange={(event) => updateBooleanField("emailEnabled", event.currentTarget.checked)}
              />
              email 補助配信
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.criticalEmailEnabled}
                onChange={(event) => updateBooleanField("criticalEmailEnabled", event.currentTarget.checked)}
              />
              critical email
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.highEmailEnabled}
                onChange={(event) => updateBooleanField("highEmailEnabled", event.currentTarget.checked)}
              />
              high email
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.serviceMessageEnabled}
                onChange={(event) => updateBooleanField("serviceMessageEnabled", event.currentTarget.checked)}
              />
              service message
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={prefsForm.digestEnabled}
                onChange={(event) => updateBooleanField("digestEnabled", event.currentTarget.checked)}
              />
              digest
            </label>
            <label style={{ display: "grid", gap: 6 }}>
              quietHours
              <input
                type="text"
                value={prefsForm.quietHours}
                placeholder="例: 22:00-07:00"
                onChange={(event) =>
                  setPrefsForm((current) => (current ? { ...current, quietHours: event.currentTarget.value } : current))
                }
                style={{ padding: 10, borderRadius: 10, border: "1px solid #d9d9d9" }}
              />
            </label>
            <button type="button" onClick={() => void handleSavePrefs()} disabled={saveState === "saving"}>
              {saveState === "saving" ? "保存中..." : "通知設定を保存"}
            </button>
            {saveState === "saved" && notifications?.prefs ? (
              <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
                更新日時: {formatDateTime(notifications.prefs.updatedAt)}
              </p>
            ) : null}
            {saveError ? <p style={{ margin: 0 }}>{saveError}</p> : null}
          </section>
        ) : null}

        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>受信トレイ</h2>
          {notifications?.items.length === 0 ? <p style={{ margin: 0 }}>通知はありません。</p> : null}
          {notifications?.items.map((item) => (
            <NotificationItemCard key={item.itemId} item={item} />
          ))}
        </section>
      </div>
    </PageScaffold>
  );
}

export { FamilyVideoDetailPage } from "./FamilyVideoDetailPage";
export { StudentDetailPage } from "./StudentDetailPage";
